// ─────────────────────────────────────────────────────────────
// profileModalConfig.ts
// Single source of truth for all Profile Options modal config.
// To add a new modal: add a key to the enum + an entry in the map.
// ─────────────────────────────────────────────────────────────

// ----------- ModalId Enum ---------------

export const enum ModalId {
  // ── Account ─────────────────────────────
  UserSecurity = "userSecurity",
  UserProfileEditMode = "userprofileeditmode",
  UserProfileEditModeSupport = "userprofileeditmodesupport",
  DeleteAccount = "deleteAccount",
  UserAddress = "userAddress",

  // ── Preferences ─────────────────────────
  UnitsAndRegion = "unitsandregion",
  Notification = "notification",
  UserLanguage = "userlanguage",

  // ── Billing ─────────────────────────────
  BillingPlans = "billingPlans",
  Invoices = "invoices",
  PaymentMethod = "paymentMethod",

  // ── Support ─────────────────────────────
  MyTickets = "myTickets",
  HelpCenter = "helpCenter",
  ContactSupport = "contactSupport",



  // ── None ────────────────────────────────
  None = "none",
}

// ── 1. Enum ──────────────────────────────────────────────────
export const enum ProfileOptionsModalType {
  // Account
  Security        = "security",
  UserAccount     = "userAccount",
  DeleteAccount   = "deleteAccount",

  // Preferences
  Units           = "units",
  Notification    = "notification",
  Language        = "language",

  // Billing
  BillingPlans    = "billingPlans",
  Invoices        = "invoices",
  PaymentMethod   = "paymentMethod",

  // Support
  MyTickets       = "myTickets",
  HelpCenter      = "helpCenter",
  ContactSupport  = "contactSupport",

  // Closed / reset state
  None            = "none",
}

// ── 2. Shared type (keep in sync with your Redux slice) ───────
export interface ModalHeaderFooter { title: string,btnTxt: string, secondaryBtnTxt: string, isheader: boolean, modalid: string, data: unknown | null, }

// ── 3. Config map ─────────────────────────────────────────────
//  • Every key maps to a *full* ModalHeaderFooter object so
//    TypeScript will warn you if a field is missing.
//  • "as const" keeps the values narrowly typed for future use.
type ModalConfigMap = Readonly<Record<ProfileOptionsModalType, ModalHeaderFooter>>

export const PROFILE_MODAL_CONFIG: ModalConfigMap = {
  // ── Account ────────────────────────────────────────────────
  [ProfileOptionsModalType.Security]: { title: "User Security", btnTxt: "Save", secondaryBtnTxt: "", isheader: true, modalid: "userSecurity", data: null },
  [ProfileOptionsModalType.UserAccount]: { title: "Account Information", btnTxt: "Save", secondaryBtnTxt: "", isheader: true, modalid: "userprofileeditmode", data: null },
  [ProfileOptionsModalType.DeleteAccount]: { title: "Review your proposal", btnTxt: "Continue to Agreement", secondaryBtnTxt: "Talk to Support", isheader: true, modalid: "deleteAccount", data: null },

  // ── Preferences ────────────────────────────────────────────
  [ProfileOptionsModalType.Units]: { title: "Units & Region", btnTxt: "Save", secondaryBtnTxt: "", isheader: true, modalid: "unitsandregion", data: null },
  [ProfileOptionsModalType.Notification]: { title: "Settings", btnTxt: "Edit", secondaryBtnTxt: "", isheader: true, modalid: "notification", data: null },
  [ProfileOptionsModalType.Language]: { title: "Language", btnTxt: "Update", secondaryBtnTxt: "", isheader: true, modalid: "userlanguage", data: null },

  // ── Billing ────────────────────────────────────────────────
  [ProfileOptionsModalType.BillingPlans]: { title: "Subscription & Billing", btnTxt: "", secondaryBtnTxt: "", isheader: true, modalid: "billingPlans", data: null },
  [ProfileOptionsModalType.Invoices]: { title: "Invoices", btnTxt: "Update", secondaryBtnTxt: "", isheader: true, modalid: "invoices", data: null },
  [ProfileOptionsModalType.PaymentMethod]: { title: "Payment Method", btnTxt: "Update", secondaryBtnTxt: "", isheader: true, modalid: "paymentMethod", data: null },

  // ── Support ────────────────────────────────────────────────
  [ProfileOptionsModalType.MyTickets]: { title: "My Tickets", btnTxt: "Update", secondaryBtnTxt: "", isheader: true, modalid: "myTickets", data: null },
  [ProfileOptionsModalType.HelpCenter]: { title: "Help Center", btnTxt: "Update", secondaryBtnTxt: "", isheader: true, modalid: "helpCenter", data: null },
  [ProfileOptionsModalType.ContactSupport]: { title: "Contact Support", btnTxt: "Update", secondaryBtnTxt: "", isheader: true, modalid: "contactSupport", data: null },

  // ── None / reset ───────────────────────────────────────────
  [ProfileOptionsModalType.None]: { title: "", btnTxt: "", secondaryBtnTxt: "", isheader: false, modalid: "", data: null },
} as const;


// ── 4. Helper — replaces the entire switch block ──────────────
export const MODAL_RESET: ModalHeaderFooter = PROFILE_MODAL_CONFIG[ProfileOptionsModalType.None]

export function getModalConfig(type: ProfileOptionsModalType | null,): ModalHeaderFooter {
  if (type === null) return MODAL_RESET
  return PROFILE_MODAL_CONFIG[type] ?? MODAL_RESET
}